import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';
const KEY_STORAGE = 'btr_admin_api_key';

function AdminKeyGate({ onAuthed }) {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function validateKey(event) {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/admin/player-profile/change-requests?status=pending&limit=1`, {
        headers: { 'x-api-key': apiKey },
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || `HTTP ${response.status}`);
      sessionStorage.setItem(KEY_STORAGE, apiKey);
      onAuthed(apiKey);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={validateKey} className="max-w-xl rounded-xl border border-outline-variant/20 bg-surface-container p-6 space-y-4">
      <div>
        <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-2">Admin</p>
        <h2 className="font-headline font-expanded text-3xl font-bold tracking-tight text-on-surface">Admin Access</h2>
      </div>
      <input
        type="password"
        value={apiKey}
        placeholder="Admin API key"
        onChange={(event) => setApiKey(event.target.value)}
        className="w-full min-h-[44px] rounded-lg border border-outline-variant/30 bg-surface-container-high px-3 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/50 transition-colors duration-150 focus:outline-none focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/70"
      />
      {error && (
        <div className="rounded-lg border border-error/40 bg-error-container/50 px-3 py-2.5 text-[13px] font-semibold text-on-error-container">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={!apiKey || loading}
        className="min-h-[44px] rounded-lg bg-primary px-5 text-sm font-bold text-on-primary transition-all duration-150 hover:bg-primary-fixed active:scale-[0.99] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
      >
        {loading ? 'Checking...' : 'Unlock Admin'}
      </button>
    </form>
  );
}

function AdminCard({ to, icon, title, description, count }) {
  return (
    <Link
      to={to}
      className="group rounded-xl border border-outline-variant/20 bg-surface-container p-6 transition-all duration-150 hover:border-primary/40 hover:bg-surface-container-high active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-3">
          <span className="material-symbols-outlined text-3xl text-primary">{icon}</span>
          <div>
            <h3 className="font-headline font-expanded text-xl font-bold text-on-surface">{title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-on-surface-variant">{description}</p>
          </div>
        </div>
        {count !== null && count !== undefined && (
          <span className="rounded-lg border border-outline-variant/30 bg-surface-container-highest px-3 py-2 font-headline text-sm font-bold text-primary tabular-nums">
            {count}
          </span>
        )}
      </div>
    </Link>
  );
}

export default function AdminHome() {
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [counts, setCounts] = useState({ claims: null, requests: null });
  const [error, setError] = useState('');

  const headers = useMemo(() => ({ 'x-api-key': authKey }), [authKey]);

  const fetchCounts = useCallback(async () => {
    if (!authKey) return;
    setError('');

    try {
      const [claimsRes, requestsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/player-profile/claims?status=pending&limit=200`, { headers }),
        fetch(`${API_BASE}/api/admin/player-profile/change-requests?status=pending&limit=200`, { headers }),
      ]);
      const [claimsJson, requestsJson] = await Promise.all([
        claimsRes.json().catch(() => null),
        requestsRes.json().catch(() => null),
      ]);
      if (!claimsRes.ok || !claimsJson?.success) throw new Error(claimsJson?.error || `Claims HTTP ${claimsRes.status}`);
      if (!requestsRes.ok || !requestsJson?.success) throw new Error(requestsJson?.error || `Requests HTTP ${requestsRes.status}`);
      setCounts({
        claims: claimsJson.claims?.length || 0,
        requests: requestsJson.requests?.length || 0,
      });
      sessionStorage.setItem(KEY_STORAGE, authKey);
    } catch (err) {
      setError(err.message);
      sessionStorage.removeItem(KEY_STORAGE);
      setAuthKey('');
    }
  }, [authKey, headers]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  if (!authKey) {
    return <AdminKeyGate onAuthed={setAuthKey} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant mb-2">Admin</p>
          <h2 className="font-headline font-expanded text-3xl sm:text-4xl font-bold tracking-tight text-on-surface">Admin Console</h2>
          <p className="text-sm text-on-surface-variant mt-2 max-w-2xl leading-relaxed">Manage profile moderation and direct player profile edits.</p>
        </div>
        <button
          type="button"
          onClick={() => {
            sessionStorage.removeItem(KEY_STORAGE);
            setAuthKey('');
          }}
          className="self-start md:self-auto min-h-[44px] rounded-lg border border-outline-variant/30 bg-surface-container px-4 text-sm font-semibold text-on-surface-variant transition-colors duration-150 hover:border-outline-variant/50 hover:text-on-surface active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          Lock Admin
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-error/40 bg-error-container/50 px-4 py-3 text-sm font-semibold text-on-error-container">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <AdminCard
          to="/admin/profile-moderation"
          icon="rule"
          title="Moderation Queue"
          description="Review player claims, corrections, profile updates, social links, and privacy requests."
          count={(counts.claims || 0) + (counts.requests || 0)}
        />
        <AdminCard
          to="/admin/player-profiles"
          icon="manage_accounts"
          title="Player Profile Editor"
          description="Search a player, edit public profile fields directly, and save as an admin-verified update."
        />
      </div>
    </div>
  );
}
